/**
 * JobListMain - The reusable "list body" of a job-style table surface.
 *
 * Extracted from JobListTemplate's `.job-list-template__main` region so the
 * same table treatment can be reused outside the full-page template — the
 * Dashboard task list embeds this directly. Renders, in order:
 *	 - Error state (shared Alert atom with a Try Again action)
 *	 - Empty state card
 *	 - Desktop (tablet+) data table with loading skeletons, optional
 *	   per-row summary bands (renderRowHeader), and a pagination footer
 *	 - Mobile stacked-card list with its own skeletons and pager
 *
 * JobListTemplate composes this alongside its stat cards + filter sidebar,
 * so the two list pages (/work-orders, /inspections) render exactly the DOM
 * they did before the extraction. Columns are injected via ColumnConfig —
 * the same contract the template has always exposed.
 */
import React, { useMemo } from 'react';
import { Alert } from '../../atoms';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Configuration for a single table column */
export interface ColumnConfig {
	/** Unique key for React rendering */
	key: string;
	/** Column header text (uppercase, tracking-wide in the header row) */
	header: string;
	/** Custom width class (e.g., "w-48px" for the actions column) */
	widthClass?: string;
	/** Render function that returns JSX for each cell in this column */
	render: (item: any, index: number) => React.ReactNode;
}

/** Props for the JobListMain organism */
export interface JobListMainProps {
	/** Table column configurations */
	columns: ColumnConfig[];
	/** The data items to display in the table */
	items: any[];
	/** Whether data is currently loading */
	isLoading: boolean;
	/** Error message to display, or null */
	error: string | null;
	/** Current page number (1-indexed) */
	currentPage: number;
	/** Total number of pages */
	totalPages: number;
	/** Total number of items across all pages */
	totalItems: number;
	/** Number of items per page */
	perPage: number;
	/** Called when the page changes */
	onPageChange: (page: number) => void;
	/** Called when retry is requested after an error */
	onRetry: () => void;
	/** Empty state message shown when no items match */
	emptyMessage?: string;
	/** Optional render function for a full-width summary row that
	 *	appears above each data row. The Inspections list uses this
	 *	to surface Building + Customer as a header band above the
	 *	per-row table cells. When omitted only the standard row renders. */
	renderRowHeader?: (item: any, index: number) => React.ReactNode;
	/** How many skeleton rows/cards to render while loading (default 8) */
	skeletonRows?: number;
	/** Optional extra classes merged onto the root element */
	className?: string;
}

/* ============================================
 * SKELETON COMPONENTS
 * Animated loading placeholders displayed while data loads.
 * ============================================ */

/** Skeleton for a single table row */
const TableRowSkeleton: React.FC<{ columnCount: number }> = ({ columnCount }) => (
	<tr className="border-b border-brand-border">
		{Array.from({ length: columnCount }, (_, i) => (
			<td key={i} className="py-12px px-16px">
				<div className="h-14px w-80px bg-gray-100 animate-pulse rounded-4px" />
			</td>
		))}
	</tr>
);

/* ============================================
 * MAIN COMPONENT
 * ============================================ */

export const JobListMain: React.FC<JobListMainProps> = ({
	columns,
	items,
	isLoading,
	error,
	currentPage,
	totalPages,
	totalItems,
	perPage,
	onPageChange,
	onRetry,
	emptyMessage = 'No items found.',
	renderRowHeader,
	skeletonRows = 8,
	className,
}) => {
	/* Calculate pagination display values */
	const showingFrom = totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
	const showingTo = Math.min(currentPage * perPage, totalItems);

	/* Page number generation for pagination with ellipsis */
	const pageNumbers = useMemo(() => {
		if (totalPages <= 5) {
			return Array.from({ length: totalPages }, (_, i) => i + 1);
		}
		const pages: number[] = [1];
		if (currentPage > 3) pages.push(-1);
		const start = Math.max(2, currentPage - 1);
		const end = Math.min(totalPages - 1, currentPage + 1);
		for (let i = start; i <= end; i++) pages.push(i);
		if (currentPage < totalPages - 2) pages.push(-1);
		pages.push(totalPages);
		return pages;
	}, [currentPage, totalPages]);

	return (
		<div className={['job-list-main', className].filter(Boolean).join(' ')} data-ref="organism-job-list-main">
			{/* Error state — uses the shared Alert atom so list
			    pages, detail pages, and workflow forms all show
			    the same error treatment. */}
			{error && !isLoading && (
				<Alert
					variant="error"
					className="mb-16px"
					action={
						<button type="button" onClick={onRetry} className="text-14px font-medium text-brand-primary hover:text-brand-primary-hover transition-colors">Try Again</button>
					}
				>
					{error}
				</Alert>
			)}

			{/* Empty state */}
			{!isLoading && !error && items.length === 0 && (
				<div className="bg-white rounded-12px border border-brand-border p-24px text-center">
					<p className="text-14px text-brand-text-muted py-32px">{emptyMessage}</p>
				</div>
			)}

			{/* Table container */}
			{(isLoading || (!error && items.length > 0)) && (
				<div className="hidden tablet:block bg-white rounded-12px border border-brand-border shadow-sm overflow-hidden">
					<div className="overflow-x-auto">
						<table className="w-full text-14px font-sans">
							<thead>
								<tr className="bg-brand-surface border-b border-brand-border">
									{columns.map((col) => (
										<th key={col.key} className={`text-left py-12px px-16px font-semibold text-brand-text-muted text-12px uppercase tracking-wide ${col.widthClass || ''}`}>{col.header}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{isLoading ? (
									/* Loading skeleton rows */
									Array.from({ length: skeletonRows }, (_, i) => (
										<TableRowSkeleton key={i} columnCount={columns.length} />
									))
								) : (
									/* Rendered data rows. When the caller supplies
									   renderRowHeader, each item produces a pair of
									   rows: a full-width "summary band" (e.g.
									   Building · Customer for inspections) followed
									   by the standard columned row. The band has a
									   subtle surface tint and no bottom border so
									   the pair reads as a single record. */
									items.flatMap((item, index) => {
										const key = item.id ?? index;
										const dataRow = (
											<tr key={`row-${key}`} className={`${renderRowHeader ? '' : 'border-b border-brand-border'} hover:bg-brand-surface transition-colors`}>
												{columns.map((col) => (
													<td key={col.key} className={`${renderRowHeader ? 'pt-4px pb-12px' : 'py-12px'} px-16px`}>{col.render(item, index)}</td>
												))}
											</tr>
										);
										if (!renderRowHeader) return [dataRow];
										const headerRow = (
											<tr key={`hdr-${key}`} className="bg-brand-surface/40 border-t border-brand-border">
												<td colSpan={columns.length} className="px-16px pt-12px pb-4px">
													{renderRowHeader(item, index)}
												</td>
											</tr>
										);
										/* The visual bottom border belongs to the
										   pair, not the data row, so we add a
										   spacer row that owns it. Tailwind's
										   border-b on the data row would draw
										   between the cells of THIS row and the
										   header of the NEXT one — which is what
										   we want. So just keep border-b on data row. */
										return [headerRow, (
											<tr key={`row-${key}`} className="border-b border-brand-border hover:bg-brand-surface transition-colors">
												{columns.map((col) => (
													<td key={col.key} className="pt-4px pb-12px px-16px">{col.render(item, index)}</td>
												))}
											</tr>
										)];
									})
								)}
							</tbody>
						</table>
					</div>

					{/* Pagination footer */}
					{!isLoading && totalItems > 0 && (
						<div className="job-list-main__pagination flex flex-wrap items-center justify-between gap-x-12px gap-y-8px px-16px py-12px border-t border-brand-border bg-brand-surface">
							<p className="text-12px font-heading uppercase tracking-wide text-brand-text-muted">SHOWING {showingFrom} TO {showingTo} OF {totalItems} ENTRIES</p>
							<div className="job-list-main__pager flex flex-wrap items-center justify-end gap-4px">
								{/* PREV button */}
								<button type="button" onClick={() => onPageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="px-10px py-6px min-h-44px desktop:min-h-32px text-12px font-semibold uppercase rounded-6px text-brand-text-muted hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">PREV</button>
								{/* Page number buttons */}
								{pageNumbers.map((page, index) => {
									if (page === -1) {
										return <span key={`ellipsis-${index}`} className="px-6px text-12px text-brand-text-muted">...</span>;
									}
									const isActive = page === currentPage;
									return (
										<button key={page} type="button" onClick={() => onPageChange(page)} className={['w-44px h-44px desktop:w-28px desktop:h-28px rounded-6px text-12px font-semibold transition-colors', isActive ? 'bg-brand-primary text-white' : 'text-brand-text-muted hover:bg-white'].join(' ')}>{page}</button>
									);
								})}
								{/* NEXT button */}
								<button type="button" onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="px-10px py-6px min-h-44px desktop:min-h-32px text-12px font-semibold uppercase rounded-6px text-brand-text-muted hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">NEXT</button>
							</div>
						</div>
					)}
				</div>
			)}

			{/* Mobile card list — the desktop table collapses to stacked cards below tablet */}
			{(isLoading || (!error && items.length > 0)) && (
				<div className="tablet:hidden flex flex-col gap-12px">
					{isLoading
						? Array.from({ length: skeletonRows }, (_, i) => (
							<div key={`mcard-skel-${i}`} className="bg-white border border-brand-border rounded-12px p-16px animate-pulse">
								<div className="h-14px w-3/4 bg-gray-100 rounded-4px mb-8px" />
								<div className="h-12px w-1/2 bg-gray-50 rounded-4px mb-4px" />
								<div className="h-12px w-2/3 bg-gray-50 rounded-4px" />
							</div>
						))
						: items.map((item, index) => (
							<div key={item.id ?? index} className="bg-white border border-brand-border rounded-12px p-16px shadow-sm">
								{renderRowHeader && (
									<div className="mb-12px pb-12px border-b border-brand-border">{renderRowHeader(item, index)}</div>
								)}
								<div className="flex flex-col gap-8px">
									{columns.map((col) => {
										const headerText = typeof col.header === 'string' ? col.header : '';
										return (
											<div key={col.key} className="flex items-start justify-between gap-12px">
												{headerText && (
													<span className="text-11px font-semibold uppercase tracking-wide text-brand-text-muted flex-shrink-0 pt-2px">{headerText}</span>
												)}
												<div className={`text-14px text-brand-text min-w-0 ${headerText ? 'text-right ml-auto' : 'w-full'}`}>{col.render(item, index)}</div>
											</div>
										);
									})}
								</div>
							</div>
						))}
				</div>
			)}

			{/* Pagination — mobile (the desktop pager lives inside the table card above).
			    Gated on !error too so it doesn't render orphaned below the error
			    Alert when a refetch fails while totalItems is still non-zero. */}
			{!isLoading && !error && totalItems > 0 && (
				<div className="tablet:hidden mt-12px flex flex-col gap-8px items-center bg-white rounded-12px border border-brand-border px-16px py-12px">
					<p className="text-12px font-heading uppercase tracking-wide text-brand-text-muted">SHOWING {showingFrom} TO {showingTo} OF {totalItems} ENTRIES</p>
					<div className="flex items-center gap-4px flex-wrap justify-center">
						<button type="button" onClick={() => onPageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="px-10px py-6px min-h-44px text-12px font-semibold uppercase rounded-6px text-brand-text-muted hover:bg-brand-surface disabled:opacity-40 disabled:cursor-not-allowed transition-colors">PREV</button>
						{pageNumbers.map((page, index) => {
							if (page === -1) {
								return <span key={`m-ellipsis-${index}`} className="px-6px text-12px text-brand-text-muted">...</span>;
							}
							const isActive = page === currentPage;
							return (
								<button key={page} type="button" onClick={() => onPageChange(page)} className={['w-44px h-44px rounded-6px text-12px font-semibold transition-colors', isActive ? 'bg-brand-primary text-white' : 'text-brand-text-muted hover:bg-brand-surface'].join(' ')}>{page}</button>
							);
						})}
						<button type="button" onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="px-10px py-6px min-h-44px text-12px font-semibold uppercase rounded-6px text-brand-text-muted hover:bg-brand-surface disabled:opacity-40 disabled:cursor-not-allowed transition-colors">NEXT</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default JobListMain;
